import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import styles from './style.scss';
import {classMap} from 'lit/directives/class-map.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {isNumber, isUndefined} from '@gl/lit/utils/index.js';

// Constants
export const BADGE_ELEMENT_TAG = 'fly-badge';

// Enums
export enum BadgeVariants {
  PRIMARY = 'primary',
  PRIMARY_LIGHT = 'primary--light',
  SECONDARY = 'secondary',
  SECONDARY_LIGHT = 'secondary--light',
  GREEN = 'green',
  GREEN_LIGHT = 'green--light',
  YELLOW = 'yellow',
  YELLOW_LIGHT = 'yellow--light',
  RED = 'red',
  RED_LIGHT = 'red--light',
  GREY = 'grey',
  GREY_INVERT = 'grey--invert',
}

export enum Size {
  XS = 'xs',
  SM = 'sm',
  BASE = 'base',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

/**
 * Component for displaying small, labelled badges.
 */
@customElement(BADGE_ELEMENT_TAG)
export default class FlyBadge extends FlywheelComponent {
  @property()
  variant: BadgeVariants = BadgeVariants.PRIMARY;

  @property()
  size: Size = Size.BASE;

  @property({type: Boolean})
  capitalize = false;

  private _maxLines?: number;
  @property({type: Number, reflect: true})
  get maxLines(): number | undefined {
    return this._maxLines;
  }
  set maxLines(lines: number | undefined) {
    if (lines !== undefined && lines <= 0) {
      console.error('max lines must be greater than 0', this);
    }
    if (isUndefined(lines) || lines > 0) this._maxLines = lines;
  }

  static get styles() {
    return [styles];
  }

  get classes(): unknown {
    return classMap({
      'fly-badge--primary': this.variant === BadgeVariants.PRIMARY,
      'fly-badge--primary--light': this.variant === BadgeVariants.PRIMARY_LIGHT,
      'fly-badge--secondary': this.variant === BadgeVariants.SECONDARY,
      'fly-badge--secondary--light':
        this.variant === BadgeVariants.SECONDARY_LIGHT,
      'fly-badge--green': this.variant === BadgeVariants.GREEN,
      'fly-badge--green--light': this.variant === BadgeVariants.GREEN_LIGHT,
      'fly-badge--yellow': this.variant === BadgeVariants.YELLOW,
      'fly-badge--yellow--light': this.variant === BadgeVariants.YELLOW_LIGHT,
      'fly-badge--red': this.variant === BadgeVariants.RED,
      'fly-badge--red--light': this.variant === BadgeVariants.RED_LIGHT,
      'fly-badge--grey': this.variant === BadgeVariants.GREY,
      'fly-badge--grey--invert': this.variant === BadgeVariants.GREY_INVERT,
      'fly-badge--capitalize': this.capitalize,

      'fly-badge--xs': this.size === Size.XS,
      'fly-badge--sm': this.size === Size.SM,
      'fly-badge--md': this.size === Size.MD,
      'fly-badge--lg': this.size === Size.LG,
      'fly-badge--xl': this.size === Size.XL,
      'fly-badge--ellipses': isNumber(this.maxLines) && this.maxLines > 0,
    });
  }

  render() {
    return html`
      <div
        style="--badge-max-lines: ${ifDefined(this._maxLines)}"
        class="fly-badge ${this.classes}"
      >
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fly-badge': FlyBadge;
  }
}
